import { useCallback } from 'react';
import { NodeProps } from 'reactflow';
import { TimePicker } from '@givebutter/primitives/date-time-picker';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { getTimeStringFromDateTime, pluralize, unmaskNumber } from '@givebutter/util';
import { faClock as faClockRegular } from '@fortawesome/pro-regular-svg-icons';
import { faClock as faClockSolid } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DelayFormValues, DelayNodeData } from 'assets/js/pages/engage/workflows/actions/types';
import * as CanvasNode from 'assets/js/pages/engage/workflows/components/CanvasNode';
import { DeleteCanvasNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DeleteCanvasNodeMenuItem';
import { DuplicateNodeMenuItem } from 'assets/js/pages/engage/workflows/components/DuplicateNodeMenuItem';
import { StepFormContainer } from 'assets/js/pages/engage/workflows/components/StepFormContainer';
import { useDebouncedAutosave } from 'assets/js/pages/engage/workflows/hooks/useDebouncedAutosave';
import { useSyncFormToNode } from 'assets/js/pages/engage/workflows/hooks/useSyncFormToNode';
import { useTriggerValidationOnDeselection } from 'assets/js/pages/engage/workflows/hooks/useTriggerValidationOnDeselection';
import * as DropdownMenu from 'components/dropdown-menu';
import * as Form from 'components/form';
import { DateTime } from 'luxon';

const presetDelayOptions = [
    { label: '1 hour after', value: '1 hour' },
    { label: '2 hours after', value: '2 hours' },
    { label: '1 day after', value: '1 day' },
    { label: '2 days after', value: '2 days' },
    { label: '1 week after', value: '1 week' },
    { label: '1 month after', value: '1 month' },
    { label: 'Custom', value: 'custom' },
];

interface DelayNodeProps extends NodeProps<DelayNodeData> {}
const Delay = ({ id, selected, data: { formData } }: DelayNodeProps) => {
    const methods = Form.useGivebutterForm<DelayFormValues>({
        mode: 'all',
        defaultValues: {
            preset: formData?.values.preset ?? '1 hour',
            custom_value: formData?.values.custom_value ?? '1',
            custom_unit: formData?.values.custom_unit ?? 'day',
            custom_time: formData?.values.custom_time ?? DateTime.now().startOf('hour'),
            skip_weekends: formData?.values.skip_weekends ?? false,
        },
    });
    const {
        control,
        formState: { errors },
        watch,
        path,
        setValue,
        trigger,
    } = methods;
    const isError = Object.keys(errors).length > 0;
    const [preset, custom_value, custom_time] = watch(['preset', 'custom_value', 'custom_time']);

    useSyncFormToNode<DelayFormValues>({ nodeId: id, control });
    useDebouncedAutosave<DelayFormValues>({ methods });
    useTriggerValidationOnDeselection<DelayFormValues>({ trigger, selected });

    const onSelectTime = useCallback(
        (time: string) => {
            const { hour, minute } = DateTime.fromFormat(time, 'T');
            const nextValue = custom_time?.set({ hour, minute });
            setValue('custom_time', nextValue, { shouldDirty: true });
        },
        [custom_time, setValue],
    );

    const isCustom = preset === 'custom';

    return (
        <Form.Provider {...methods}>
            <StepFormContainer>
                <CanvasNode.Root
                    isSelected={selected}
                    isError={isError}
                    id={id}
                    style={{
                        maxWidth: isCustom ? 500 : 384,
                    }}
                >
                    <CanvasNode.Container>
                        <CanvasNode.ErrorAlert />
                        <CanvasNode.Header>
                            <CanvasNode.Icon
                                icon={faClockSolid}
                                className="text-brand-violet-primary"
                            />
                            <CanvasNode.Title>Wait</CanvasNode.Title>
                            <DropdownMenu.Root>
                                <CanvasNode.ActionMenuTrigger />
                                <DropdownMenu.Content align="end">
                                    <DuplicateNodeMenuItem />
                                    <DeleteCanvasNodeMenuItem />
                                </DropdownMenu.Content>
                            </DropdownMenu.Root>
                        </CanvasNode.Header>
                        <CanvasNode.Body>
                            <div className="flex flex-col gap-4">
                                <Form.Container>
                                    <Form.Label name={path('preset')} className="sr-only">
                                        Wait
                                    </Form.Label>
                                    <Form.SingleSelect control={control} name="preset">
                                        <SingleSelect.Trigger aria-label="Wait" />
                                        <SingleSelect.Content>
                                            {presetDelayOptions.map(({ value, label }) => (
                                                <SingleSelect.Item value={value} key={value}>
                                                    <SingleSelect.ItemText>
                                                        {label}
                                                    </SingleSelect.ItemText>
                                                </SingleSelect.Item>
                                            ))}
                                        </SingleSelect.Content>
                                    </Form.SingleSelect>
                                </Form.Container>

                                {isCustom && typeof custom_value !== 'undefined' ? (
                                    <div className="flex items-center gap-2">
                                        <Form.Container className="flex min-w-0 flex-row flex-nowrap gap-0">
                                            <Form.Label
                                                name={path('custom_value')}
                                                className="sr-only"
                                            >
                                                Custom Value
                                            </Form.Label>
                                            <Form.TextInputRoot
                                                name="custom_value"
                                                control={control}
                                                inputRootProps={{
                                                    mask: 'whole-number',
                                                    className: 'input-left flex-shrink min-w-0',
                                                }}
                                            >
                                                <FontAwesomeIcon
                                                    icon={faClockRegular}
                                                    className="text-gray-500"
                                                />
                                                <Form.TextInputForm />
                                            </Form.TextInputRoot>
                                            <Form.Label
                                                name={path('custom_unit')}
                                                className="sr-only"
                                            >
                                                Custom unit
                                            </Form.Label>
                                            <Form.SingleSelect
                                                control={control}
                                                name="custom_unit"
                                                onValueChange={value => {
                                                    if (value === 'hour') {
                                                        setValue('custom_time', null);
                                                    } else {
                                                        setValue(
                                                            'custom_time',
                                                            DateTime.now().startOf('hour'),
                                                        );
                                                    }
                                                }}
                                            >
                                                <SingleSelect.Trigger
                                                    aria-label="Custom unit"
                                                    className="input-right w-fit"
                                                >
                                                    <SingleSelect.Value placeholder="Select unit" />
                                                </SingleSelect.Trigger>
                                                <SingleSelect.Content>
                                                    <SingleSelect.Item value="hour">
                                                        <SingleSelect.ItemText>
                                                            {`${pluralize(
                                                                unmaskNumber(custom_value),
                                                                'Hour',
                                                            )} after`}
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="day">
                                                        <SingleSelect.ItemText>
                                                            {`${pluralize(
                                                                unmaskNumber(custom_value),
                                                                'Day',
                                                            )} after`}
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="week">
                                                        <SingleSelect.ItemText>
                                                            {`${pluralize(
                                                                unmaskNumber(custom_value),
                                                                'Week',
                                                            )} after`}
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                    <SingleSelect.Item value="month">
                                                        <SingleSelect.ItemText>
                                                            {`${pluralize(
                                                                unmaskNumber(custom_value),
                                                                'Month',
                                                            )} after`}
                                                        </SingleSelect.ItemText>
                                                    </SingleSelect.Item>
                                                </SingleSelect.Content>
                                            </Form.SingleSelect>
                                        </Form.Container>
                                        {custom_time ? (
                                            <>
                                                <span>at</span>
                                                <Form.Container className="max-w-40">
                                                    <Form.Label
                                                        name={path('custom_time')}
                                                        className="sr-only"
                                                    >
                                                        Custom time
                                                    </Form.Label>
                                                    <TimePicker
                                                        value={getTimeStringFromDateTime(
                                                            custom_time,
                                                        )}
                                                        onValueChange={onSelectTime}
                                                    />
                                                </Form.Container>
                                            </>
                                        ) : null}
                                    </div>
                                ) : null}

                                <Form.Container className="flex flex-row gap-2">
                                    <Form.UseFormCheckboxInput
                                        id={`skip_weekends_${id}`}
                                        name="skip_weekends"
                                        control={control}
                                    />
                                    <Form.Label
                                        id={`skip_weekends_${id}`}
                                        name={path('skip_weekends')}
                                        className="text-gray-700"
                                    >
                                        Skip weekends
                                    </Form.Label>
                                </Form.Container>
                            </div>
                        </CanvasNode.Body>
                    </CanvasNode.Container>
                </CanvasNode.Root>
            </StepFormContainer>
        </Form.Provider>
    );
};

export { Delay };
export type { DelayNodeProps };
